import { useState } from 'react';
import { type FieldFilter, type FieldOp, makeFieldFilter } from '../utils/payloadQuery';
import { type JsonPick, renderJson } from '../utils/renderJson';
import { JsonPickMenu, type PendingPick } from './JsonPickMenu';

interface Props {
  payload: unknown;
  raw: string;
  mode: 'json' | 'raw';
  /** When provided, every value in the JSON view becomes a click target that
   *  turns into a filter chip. Omitted in read-only contexts (Debug panel). */
  onFilter?: (f: FieldFilter) => void;
}

export function JsonView({ payload, raw, mode, onFilter }: Props) {
  const [pending, setPending] = useState<PendingPick | null>(null);

  // Text, booleans and null can only mean one thing, so they filter on the
  // first click. Numbers get a 3-item menu — the only place the user picks a
  // comparison, and they pick it by reading a sentence, not an operator.
  const handlePick: JsonPick = (path, value, anchor) => {
    if (typeof value === 'number') {
      setPending({ path, value, x: anchor.x, y: anchor.y });
      return;
    }
    onFilter?.(makeFieldFilter(path, 'is', value));
  };

  const choose = (op: FieldOp) => {
    if (pending) onFilter?.(makeFieldFilter(pending.path, op, pending.value));
    setPending(null);
  };

  return (
    <div className="ds-json flex-1 overflow-auto p-3 font-mono text-[0.769em] leading-[1.65] text-(--color-text)">
      {mode === 'raw' ? (
        <pre className="m-0 whitespace-pre-wrap break-all">
          {(() => {
            try {
              return JSON.stringify(JSON.parse(raw), null, 2);
            } catch {
              return raw;
            }
          })()}
        </pre>
      ) : (
        <pre className="m-0 whitespace-pre-wrap break-words">
          {renderJson(payload, 0, onFilter ? { onPick: handlePick } : {})}
        </pre>
      )}
      {pending && (
        <JsonPickMenu pick={pending} onChoose={choose} onClose={() => setPending(null)} />
      )}
    </div>
  );
}
